import api from './api';

/**
 * Servicio para gestión de futbolistas en equipos
 * Maneja las operaciones de mover, sustituir y desasignar jugadores
 */

const futbolistaEnEquipoService = {
  /**
   * Mover jugador de un equipo a otro
   * @param {Object} data - Datos del movimiento
   * @param {number} data.futbolista_id - ID del futbolista
   * @param {number} data.equipo_origen_id - ID del equipo origen
   * @param {number} data.equipo_destino_id - ID del equipo destino
   * @param {number} [data.posicion_asignada] - ID de la nueva posición (opcional)
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async moverJugador(data) {
    try {
      const response = await api.put('/postgres/futbolista-equipo/mover', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al mover jugador:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al mover jugador',
        details: error.response?.data
      };
    }
  },

  /**
   * Sustituir dos jugadores entre equipos
   * @param {Object} data - Datos de la sustitución
   * @param {number} data.futbolista_1_id - ID del primer futbolista
   * @param {number} data.equipo_1_id - ID del equipo del primer futbolista
   * @param {number} data.futbolista_2_id - ID del segundo futbolista
   * @param {number} data.equipo_2_id - ID del equipo del segundo futbolista
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async sustituirJugadores(data) {
    try {
      const response = await api.put('/postgres/futbolista-equipo/sustituir', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al sustituir jugadores:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al sustituir jugadores',
        details: error.response?.data
      };
    }
  },

  /**
   * Desasignar jugador de equipo (enviar a bolsa de no asignados)
   * @param {Object} data - Datos de la desasignación
   * @param {number} data.futbolista_id - ID del futbolista
   * @param {number} data.equipo_id - ID del equipo
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async desasignarJugador(data) {
    try {
      const response = await api.delete('/postgres/futbolista-equipo/desasignar', {
        data: data
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al desasignar jugador:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al desasignar jugador',
        details: error.response?.data
      };
    }
  },

  /**
   * Asignar jugador no asignado a un equipo
   * @param {Object} data - Datos de la asignación
   * @param {number} data.futbolista_id - ID del futbolista
   * @param {number} data.equipo_id - ID del equipo destino
   * @param {number} [data.posicion_asignada] - ID de la posición asignada (opcional)
   * @param {boolean} [data.es_titular] - Si es titular o no (opcional)
   * @returns {Promise<Object>} Respuesta del servidor
   */
  async asignarJugador(data) {
    try {
      const response = await api.post('/postgres/futbolista-equipo/asignar', data);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Error al asignar jugador:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al asignar jugador',
        details: error.response?.data
      };
    }
  },

  /**
   * Obtener posiciones disponibles de un futbolista
   * @param {number} futbolistaId - ID del futbolista
   * @returns {Promise<Object>} Posiciones disponibles del futbolista
   */
  async getPosicionesFutbolista(futbolistaId) {
    try {
      const response = await api.get(`/postgres/futbolista-equipo/futbolista/${futbolistaId}/posiciones`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error al obtener posiciones del futbolista:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener posiciones del futbolista',
        data: { posiciones: [] }
      };
    }
  },

  /**
   * Obtener equipos disponibles para mover un jugador
   * @param {number} partidoId - ID del partido actual (opcional, para incluir equipos del mismo partido)
   * @param {number} equipoActualId - ID del equipo actual del jugador (para excluirlo)
   * @returns {Promise<Object>} Lista de equipos disponibles
   */
  async getEquiposDisponibles(partidoId = null, equipoActualId = null) {
    try {
      const params = new URLSearchParams();
      if (partidoId) params.append('partido_id', partidoId);
      if (equipoActualId) params.append('excluir_equipo_id', equipoActualId);

      const response = await api.get(`/postgres/equipos/disponibles?${params.toString()}`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error al obtener equipos disponibles:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener equipos disponibles',
        data: []
      };
    }
  },

  /**
   * Obtener jugadores de un equipo específico
   * @param {number} equipoId - ID del equipo
   * @returns {Promise<Object>} Lista de jugadores del equipo
   */
  async getJugadoresEquipo(equipoId) {
    try {
      const response = await api.get(`/postgres/equipos/${equipoId}/jugadores`);
      return {
        success: true,
        data: response.data.data || []
      };
    } catch (error) {
      console.error('Error al obtener jugadores del equipo:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Error al obtener jugadores del equipo',
        data: []
      };
    }
  },

  /**
   * Validar si un movimiento es posible
   * @param {Object} data - Datos del movimiento a validar
   * @returns {Promise<Object>} Resultado de la validación
   */
  async validarMovimiento(data) {
    try {
      const response = await api.post('/postgres/futbolista-equipo/validar-movimiento', data);
      return {
        success: true,
        data: response.data.data,
        valido: response.data.valido,
        mensaje: response.data.mensaje
      };
    } catch (error) {
      console.error('Error al validar movimiento:', error);
      return {
        success: false,
        valido: false,
        error: error.response?.data?.message || 'Error al validar movimiento'
      };
    }
  }
};

export default futbolistaEnEquipoService;
